import { Spin } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  DropdownOption,
  FilterDropdown,
  PermissionEmptyState,
} from "@/pages/documents/components/DocShared";
import { listAskSourceSites, type AskSourceSiteItem } from "@/services/sourcePathAsk";

function SiteSourceCard({ source, isZh }: { source: AskSourceSiteItem; isZh: boolean }) {
  return (
    <article className="flex items-start gap-4 rounded-2xl border border-gray-200 bg-white px-5 py-4 transition-colors hover:border-gray-300">
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
      >
        <i className="ri-layout-grid-line text-lg" style={{ color: "#830051" }} />
      </div>

      <div className="min-w-0 flex-1">
        <h2 className="text-sm font-bold text-gray-800">{source.SourceSite}</h2>
        <p className="mt-1 text-sm leading-relaxed text-gray-600">{source.Remark?.trim() || "-"}</p>
        <div className="mt-2 flex min-w-0 items-start gap-2">
          <p className="min-w-0 break-words font-mono text-xs leading-relaxed text-gray-400">
            {source.IncludedPaths.length > 0
              ? source.IncludedPaths.join(", ")
              : isZh
                ? "暂无 Included Path"
                : "No included paths"}
          </p>
          {source.SharePointSiteUrl ? (
            <a
              href={source.SharePointSiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${source.SourceSite} in SharePoint`}
              title={isZh ? "在 SharePoint 中打开" : "Open in SharePoint"}
              className="mt-0.5 inline-flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-md text-[#830051] transition-colors hover:bg-[#830051]/5"
            >
              <i className="ri-external-link-line text-xs" />
            </a>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export default function AskSiteSourceTab({ isZh }: { isZh: boolean }) {
  const [sources, setSources] = useState<AskSourceSiteItem[]>([]);
  const [filterSite, setFilterSite] = useState("all");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadSources = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await listAskSourceSites();
      setSources(response.data.Data ?? []);
    } catch {
      setSources([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSources();
  }, [loadSources]);

  const siteNames = useMemo(
    () =>
      [...new Set(sources.map((source) => source.SourceSite))].sort((a, b) => a.localeCompare(b)),
    [sources],
  );

  const filteredSources = useMemo(() => {
    if (filterSite === "all") return sources;
    return sources.filter((source) => source.SourceSite === filterSite);
  }, [filterSite, sources]);

  const filterLabel = filterSite === "all" ? "All Site" : filterSite;

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center" aria-label="Loading Site Sources">
        <Spin />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6">
        <PermissionEmptyState
          icon="ri-error-warning-line"
          title={isZh ? "加载失败" : "Unable to Load Site Sources"}
          desc={
            isZh
              ? "General Source 加载失败，请稍后重试。"
              : "Failed to load General Sources. Please try again."
          }
        />
        <button
          type="button"
          onClick={() => void loadSources()}
          className="cursor-pointer rounded-lg bg-[#830051] px-4 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
        >
          {isZh ? "重试" : "Retry"}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-gray-50/60">
      <div className="flex flex-wrap items-center gap-2 px-6 pb-3 pt-4">
        <FilterDropdown label={filterLabel} active={filterSite !== "all"} closeOnSelect>
          <DropdownOption
            label="All Site"
            active={filterSite === "all"}
            onClick={() => setFilterSite("all")}
          />
          {siteNames.map((siteName) => (
            <DropdownOption
              key={siteName}
              label={siteName}
              active={filterSite === siteName}
              onClick={() => setFilterSite(siteName)}
              icon="ri-layout-grid-line"
            />
          ))}
        </FilterDropdown>

        <span className="text-xs text-gray-400 whitespace-nowrap">
          {filteredSources.length}{" "}
          {isZh ? "个站点" : filteredSources.length === 1 ? "site" : "sites"}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-4">
        {filteredSources.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <PermissionEmptyState
              icon="ri-folder-open-line"
              title={isZh ? "暂无 General Source" : "No General Sources"}
              desc={
                isZh
                  ? "当前没有可访问的站点来源，请联系管理员。"
                  : "No site sources are available. Please contact your administrator."
              }
            />
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSources.map((source) => (
              <SiteSourceCard key={source.ID} source={source} isZh={isZh} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
